import React, { useRef } from "react";
import {
  arrow,
  autoUpdate,
  flip,
  FloatingFocusManager,
  offset as offsetMiddleware,
  Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { FloatingDot } from "./FloatingDot";

export interface ElementPointerProps {
  id?: string;
  target: Element;
  placement?: Placement;
  scale?: number;
  offset?: number | { mainAxis: number; crossAxis: number };
}

export function ElementPointer({ id, target, placement, offset = 10, scale = 1 }: ElementPointerProps) {
  const arrowRef = useRef(null);
  const { refs, floatingStyles, context } = useFloating({
    open: true,
    middleware: [
      offsetMiddleware(offset),
      flip({ fallbackAxisSideDirection: "end" }),
      shift(),
      arrow({ element: arrowRef }),
    ],
    whileElementsMounted: autoUpdate,
    placement: placement,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  if ((!refs.reference.current && target) || refs.reference.current !== target) {
    refs.setReference(target);
  }

  return (
    <>
      <FloatingFocusManager context={context} modal={false}>
        <div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
          <FloatingDot id={id} ref={arrowRef} context={context} scale={scale} />
        </div>
      </FloatingFocusManager>
    </>
  );
}
